import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Switch } from "@volca/ui/components/ui/switch";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { useReportCurrency } from "@/profitability/currency";
import { profitabilityTemplateSettingsPath } from "@/profitability/routes";
import { effectiveValue, formatSetting } from "@/profitability/settings/values";
import { EditRuleButton } from "@/profitability/template-page/edit-rule-button";
import type { ProfitabilityReport } from "@/profitability/types";
import { EditSetting } from "./rates-group";
import { RuleFindings } from "./rule-findings";
import { RuleHistory } from "./rule-history";
import { useRulePeriod } from "./rule-period";
import { SettingsGroup, SettingsRow } from "./settings-group";
import type { TemplateSettingsState } from "./use-settings-state";

export const RuleDetail = ({
  businessId,
  report,
  ruleId,
  state,
}: {
  businessId: string;
  report: ProfitabilityReport;
  ruleId: string;
  state: TemplateSettingsState;
}) => {
  const currency = useReportCurrency();
  const { settings, editable, saving, save } = state;
  const rules = settings?.rules ?? [];
  const rule = rules.find((item) => item.id === ruleId);
  const periods = settings?.periods ?? [];
  const period = useRulePeriod(periods);
  const [editing, setEditing] = useState<string | null>(null);
  const open = settings?.declared.find((item) => item.key === editing);
  const back = (
    <Link
      to={`${profitabilityTemplateSettingsPath(report.id, "rules")}${period.runId ? `?period=${encodeURIComponent(period.runId)}` : ""}`}
      className="flex min-h-9 w-fit items-center gap-1 text-sm text-subtle hover:text-ink focus-visible:outline-2 focus-visible:outline-ink pointer-coarse:min-h-11"
    >
      <ArrowLeftIcon aria-hidden className="rtl:rotate-180" />
      All rules
    </Link>
  );
  if (!settings)
    return (
      <Stack gap={4}>
        {back}
        <Skeleton
          className="h-32 w-full"
          role="status"
          aria-label="Loading rule"
        />
      </Stack>
    );
  if (!rule)
    return (
      <Stack gap={4}>
        {back}
        <Heading level={1} tabIndex={-1} variant="heading" className="text-2xl">
          Rule not in the current version
        </Heading>
        <Text tone="muted">
          This version no longer declares this rule. You can still inspect its
          earlier findings by period.
        </Text>
        <RuleFindings
          businessId={businessId}
          reportId={report.id}
          rule={{ id: ruleId, on_label: null }}
          periods={periods}
          runId={period.runId}
          onPeriodChange={period.select}
        />
      </Stack>
    );
  const thresholds = settings.declared.filter((item) =>
    rule.settings.includes(item.key),
  );
  const owner = settings.owners?.people.find(
    (person) => person.id === rule.owner_id,
  );
  return (
    <Stack gap={8}>
      <Stack gap={4}>
        {back}
        <Stack
          gap={3}
          className="sm:flex-row sm:items-start sm:justify-between"
        >
          <Heading
            level={1}
            tabIndex={-1}
            variant="heading"
            className="min-w-0 text-2xl text-balance [overflow-wrap:anywhere]"
          >
            {rule.label}
          </Heading>
          {editable && !report.archived_at && (
            <Stack className="shrink-0 self-start">
              <EditRuleButton
                reportId={report.id}
                rule={rule}
                runId={period.runId}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
      <SettingsGroup
        title="Current condition"
        description={rule.checks}
        footer="Changes apply to future reports. Rerun an earlier period to apply them there."
      >
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap={3}
          className="p-4"
        >
          <Text variant="label">{rule.enabled ? "Enabled" : "Disabled"}</Text>
          <Switch
            checked={rule.enabled}
            disabled={!editable || saving}
            aria-label={`Flag ${rule.label.toLowerCase()}`}
            onCheckedChange={(enabled) => {
              void save({ rules: { [rule.id]: { enabled } } }).catch(
                () => undefined,
              );
            }}
          />
        </Stack>
        {thresholds.map((setting) => (
          <SettingsRow
            key={setting.key}
            label={
              setting.label.startsWith(`${rule.label}: `)
                ? setting.label.charAt(rule.label.length + 2).toUpperCase() +
                  setting.label.slice(rule.label.length + 3)
                : setting.label
            }
            value={formatSetting(setting, effectiveValue(setting), currency)}
            hint={setting.description}
            action={
              editable && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  aria-label={`Edit ${setting.label}`}
                  onClick={() => setEditing(setting.key)}
                >
                  Edit
                </Button>
              )
            }
          />
        ))}
        {thresholds.length === 0 && editable && (
          <SettingsRow
            label="Change the condition"
            hint="Ask Volca to change the logic or add an adjustable threshold."
          />
        )}
        {owner && (
          <SettingsRow
            label="Assigned reviewer"
            value={owner.full_name}
            action={
              <Link
                to={profitabilityTemplateSettingsPath(report.id, "people")}
                className="inline-flex items-center text-sm underline underline-offset-4 pointer-coarse:min-h-11"
              >
                Manage reviewers
              </Link>
            }
          />
        )}
      </SettingsGroup>
      <RuleHistory
        rule={rule}
        periods={periods}
        runId={period.runId}
        onSelect={(id) => {
          period.select(id);
          document
            .getElementById("rule-findings-title")
            ?.scrollIntoView({ block: "start" });
        }}
      />
      <RuleFindings
        businessId={businessId}
        reportId={report.id}
        rule={rule}
        periods={periods}
        runId={period.runId}
        onPeriodChange={period.select}
      />
      {open && (
        <EditSetting
          key={open.key}
          setting={open}
          open
          onOpenChange={(next) => !next && setEditing(null)}
          onSave={(value) => save({ values: { [open.key]: value } })}
        />
      )}
    </Stack>
  );
};
